import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconCheck, IconExternalLink, IconX } from "@tabler/icons-react"

interface Balance {
  balance_type: string
  days_available: number
  days_used: number
  remaining_days: number
}

interface TimeOffRequest {
  id: number
  request_type: string
  status: string
  start_date: string
  end_date: string
  days_requested: number
  hours_requested: number | null
  request_type_unit: string
  notes: string | null
  created_at: string
  updated_at: string
  user: {
    id: number
    name: string
    email: string
    role: string
    avatar_url: string | null
  }
}

interface Props {
  [key: string]: unknown
  request: TimeOffRequest
  balance: Balance | null
}

const STATUS_BADGE: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
  approved: { variant: "default", label: "Approved" },
  pending: { variant: "secondary", label: "Pending" },
  denied: { variant: "destructive", label: "Denied" },
  cancelled: { variant: "outline", label: "Cancelled" },
}

function formatDateLong(dateStr: string) {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function durationDisplay(req: TimeOffRequest) {
  if (req.request_type_unit === "hours" && req.hours_requested) {
    return `${req.hours_requested}h`
  }
  return `${req.days_requested}d`
}

export default function AdminTimeOffRequestShow() {
  const { request, balance } = usePage<Props>().props
  const badge = STATUS_BADGE[request.status] || { variant: "outline" as const, label: request.status }

  function handleApprove() {
    if (
      confirm(
        `Approve this request?\n\nThis will deduct ${request.days_requested} days from ${request.user.name}'s ${request.request_type} balance.`,
      )
    ) {
      router.patch(`/admin/time_off_requests/${request.id}/approve`)
    }
  }

  function handleDeny() {
    if (confirm("Deny this request?")) {
      router.patch(`/admin/time_off_requests/${request.id}/deny`)
    }
  }

  const balanceUsedPct = balance ? Math.round((balance.days_used / balance.days_available) * 100) : 0
  const afterApproval = balance ? balance.remaining_days - request.days_requested : 0

  return (
    <>
      <Head title={`Request #${request.id} - Admin - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Header */}
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/admin/time_off_requests">All Requests</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Request #{request.id}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href="/admin/time_off_requests">
                  <IconArrowLeft className="h-4 w-4" />
                </a>
              </Button>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-lg font-semibold tracking-tight">
                    {request.request_type.charAt(0).toUpperCase() + request.request_type.slice(1)} Leave
                  </h1>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </div>
                <p className="text-muted-foreground text-sm">
                  {request.user.name} &middot; {formatDate(request.start_date)} &mdash; {formatDate(request.end_date)}{" "}
                  &middot; {durationDisplay(request)}
                </p>
              </div>
            </div>

            {request.status === "pending" && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleDeny}>
                  <IconX className="mr-1.5 h-4 w-4" />
                  Deny
                </Button>
                <Button size="sm" onClick={handleApprove}>
                  <IconCheck className="mr-1.5 h-4 w-4" />
                  Approve
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main */}
          <div className="lg:col-span-2 space-y-6">
            {/* Details Grid */}
            <div className="rounded-xl border bg-card">
              <div className="grid grid-cols-2 gap-px sm:grid-cols-4">
                <DetailCell label="Type" value={request.request_type} capitalize />
                <DetailCell
                  label="Duration"
                  value={
                    request.request_type_unit === "hours" && request.hours_requested
                      ? `${request.hours_requested} hours`
                      : `${request.days_requested} days`
                  }
                />
                <DetailCell label="From" value={formatDateLong(request.start_date)} />
                <DetailCell label="To" value={formatDateLong(request.end_date)} />
              </div>

              {request.notes && (
                <>
                  <Separator />
                  <div className="p-5">
                    <p className="text-muted-foreground mb-1.5 text-xs font-medium uppercase tracking-wider">Notes</p>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{request.notes}</p>
                  </div>
                </>
              )}

              <Separator />
              <div className="grid grid-cols-2 gap-px">
                <DetailCell label="Submitted" value={formatDateTime(request.created_at)} />
                <DetailCell label="Last Updated" value={formatDateTime(request.updated_at)} />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Employee */}
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={request.user.avatar_url || undefined} alt={request.user.name} />
                  <AvatarFallback className="text-xs">{request.user.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-tight">{request.user.name}</p>
                  <p className="text-muted-foreground text-xs">{request.user.email}</p>
                </div>
              </div>
              <div className="mt-3">
                <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2 h-7 text-xs" asChild>
                  <a href={`/admin/users/${request.user.id}`}>
                    View profile
                    <IconExternalLink className="ml-1 h-3 w-3" />
                  </a>
                </Button>
              </div>
            </div>

            {/* Balance */}
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-3 text-sm font-medium">
                {request.request_type.charAt(0).toUpperCase() + request.request_type.slice(1)} Balance
              </h3>
              {balance ? (
                <div className="space-y-3">
                  {/* Progress bar */}
                  <div>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="text-2xl font-semibold tabular-nums">{balance.remaining_days}</span>
                      <span className="text-muted-foreground text-xs">of {balance.days_available} days</span>
                    </div>
                    <div className="bg-muted mt-2 h-1.5 w-full overflow-hidden rounded-full">
                      <div
                        className="bg-primary h-full rounded-full transition-all"
                        style={{ width: `${Math.min(100, 100 - balanceUsedPct)}%` }}
                      />
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">{balance.days_used} days used</p>
                  </div>

                  {request.status === "pending" && (
                    <>
                      <Separator />
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">After approval</span>
                        <span className={`font-semibold tabular-nums ${afterApproval < 0 ? "text-destructive" : ""}`}>
                          {afterApproval.toFixed(1)} days
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-3">
                  <p className="text-muted-foreground text-sm">No balance configured</p>
                  <p className="text-destructive mt-0.5 text-xs">Cannot approve without balance</p>
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-3 text-sm font-medium">Quick Links</h3>
              <div className="space-y-1">
                <QuickLink href={`/admin/time_off_requests?user_id=${request.user.id}`}>
                  All requests from {request.user.name}
                </QuickLink>
                <QuickLink href={`/admin/users/${request.user.id}`}>Manage balances</QuickLink>
                <QuickLink href="/admin/time_off_requests?status=pending">All pending requests</QuickLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function DetailCell({ label, value, capitalize: cap }: { label: string; value: string; capitalize?: boolean }) {
  return (
    <div className="p-5">
      <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-medium ${cap ? "capitalize" : ""}`}>{value}</p>
    </div>
  )
}

function QuickLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-muted -mx-2"
    >
      <IconExternalLink className="h-3 w-3 shrink-0" />
      {children}
    </a>
  )
}

AdminTimeOffRequestShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
